/**
 * RevenueChart
 * Bar chart showing revenue over time using Recharts
 */

import { memo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Banknote } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/core/components/ui/card';
import { Skeleton } from '@/core/components/ui/skeleton';
import { RevenueDataPoint } from '../../types/dashboard-types';
import { useCurrencyFormatterRight } from '../../hooks/useCurrencyFormatter';

interface RevenueChartProps {
  data: RevenueDataPoint[];
  isLoading?: boolean;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: RevenueDataPoint;
  }>;
  label?: string;
  formatCurrency: (value: number) => string;
}

function CustomTooltip({ active, payload, label, formatCurrency }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0].payload;
  const formattedDate = new Date(label || '').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="bg-white p-3 shadow-lg rounded-lg border">
      <p className="text-sm font-medium text-gray-900 mb-2">{formattedDate}</p>
      <div className="space-y-1">
        <p className="text-sm text-gray-600">
          Revenue: <span className="font-medium text-gray-900">{formatCurrency(data.revenueIls)}</span>
        </p>
        <p className="text-sm text-gray-600">
          Transactions: <span className="font-medium text-gray-900">{data.transactionsCount}</span>
        </p>
        <p className="text-sm text-gray-600">
          Avg Transaction: <span className="font-medium text-gray-900">{formatCurrency(data.averageTransactionIls)}</span>
        </p>
      </div>
    </div>
  );
}

export const RevenueChart = memo(function RevenueChart({ data, isLoading = false }: RevenueChartProps) {
  const formatCurrency = useCurrencyFormatterRight();

  // Format data for chart
  const chartData = data.map((point) => ({
    ...point,
    date: new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    fullDate: point.date,
  }));

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Banknote className="h-5 w-5 text-green-500" />
            Revenue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Banknote className="h-5 w-5 text-green-500" />
            Revenue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-gray-500">
            No revenue data available for the selected period
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Banknote className="h-5 w-5 text-green-500" />
          Revenue
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: '#6B7280' }}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#6B7280' }}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={(props) => <CustomTooltip {...props} formatCurrency={formatCurrency} />} />
              <Bar
                dataKey="revenueIls"
                fill="#10B981"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
});
